/**
 * O corpo de uma tarefa aberta — o desenho que a frente do roadmap e o card do Dev Board
 * compartilham.
 *
 * <p><b>Saiu das abas.</b> Antes, descrição, checklist, anexos e conversa moravam em três
 * abas: ver o checklist escondia a conversa, e ver a conversa escondia os anexos. Numa
 * tarefa, essas quatro coisas são a <b>mesma</b> pergunta — "em que pé isto está" — e
 * separá-las obrigava a clicar três vezes para responder uma. Agora é uma rolagem só, com
 * seções tituladas, como num quadro de tarefas de mercado.
 *
 * <p>A aba tinha uma virtude que não se pode perder: dizer <b>quanto</b> tem em cada
 * lugar sem abrir. Por isso cada seção carrega o número no próprio título — "Checklist
 * 3/7", "Anexos 2" —, e é o que faz a rolagem não virar uma caixa-surpresa.
 *
 * <p><b>Ações à direita, conteúdo à esquerda — e o resto embaixo.</b> A coluna estreita
 * junta o que se <i>faz</i> com a tarefa (quem responde, concluir, mudar de coluna ou de
 * janela, excluir); a larga tem o que se <i>escreve</i> sobre ela. Misturados, como
 * estavam, o botão de excluir ficava a um palmo do campo de descrição.
 *
 * <p>Só que a lateral é curta e acaba muito antes do conteúdo. Por isso <b>Anexos e
 * Atividade descem para a largura inteira</b>: eles são as duas seções que mais pedem
 * espaço — nome de arquivo e comentário longo —, e ficar na coluna estreita deixava uma
 * faixa vazia à direita exatamente na altura deles.
 *
 * <p>O que <b>não</b> mudou: as quatro peças de dentro continuam sendo os mesmos
 * componentes que as duas telas já dividiam (`DescricaoDaTarefa`, `ChecklistDaTarefa`,
 * `AnexosDaTarefa`, `Conversa`). Isto aqui é a moldura — e é justamente por
 * ela ter sido copiada nas duas telas que dava para as duas divergirem.
 */
import type { ReactNode } from 'react';

import type { Membro } from '../api/tipos';
import { AnexosDaTarefa, useQuantosAnexos } from './AnexosDaTarefa';
import { ChecklistDaTarefa, useResumoDoChecklist } from './ChecklistDaTarefa';
import { Conversa } from './Conversa';
import { DescricaoDaTarefa } from './DescricaoDaTarefa';
import { Avatar } from '../comum/Avatar';

/**
 * Uma seção do corpo: ícone, título, e um número à direita quando há o que contar.
 *
 * <p>O ícone é o que permite achar a seção rolando sem ler: numa coluna de quatro
 * títulos do mesmo tamanho, a forma é reconhecida antes da palavra.
 */
function Secao({
  icone,
  titulo,
  contagem,
  children,
}: {
  icone: string;
  titulo: string;
  contagem?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="tf-secao">
      <h3 className="tf-secao-tit">
        <span className="tf-secao-ic" aria-hidden="true">
          {icone}
        </span>
        {titulo}
        {contagem != null && <span className="tf-secao-n">{contagem}</span>}
      </h3>
      {children}
    </section>
  );
}

export function CorpoDaTarefa({
  tarefaId,
  membros,
  descricao,
  aoSalvarDescricao,
  concluida,
  aoAlternarConcluida,
  rotuloConcluida,
  responsaveis,
  aoAlternarResponsavel,
  comentarios,
  contexto,
  aoExcluir,
}: {
  tarefaId: number;
  membros: Membro[];
  descricao: string | null | undefined;
  aoSalvarDescricao(descricao: string): void;
  concluida: boolean;
  aoAlternarConcluida(): void;
  /** "Card" ou "Frente" — o que se está marcando como concluído. */
  rotuloConcluida: string;
  responsaveis: number[];
  aoAlternarResponsavel(membroId: number): void;
  /** Quantos comentários a tarefa tem — já vem no card e na frente. */
  comentarios: number;
  /**
   * O que só existe num dos dois: a coluna, no card; o bloco e a janela de meses, na
   * frente. Entra na lateral porque é uma <b>escolha sobre</b> a tarefa, e não algo que
   * se escreve nela.
   */
  contexto: ReactNode;
  aoExcluir(): void;
}) {
  const { total, feitos } = useResumoDoChecklist(tarefaId);
  const quantosAnexos = useQuantosAnexos(tarefaId);

  return (
    <div className="tf-corpo">
      <div className="tf-principal">
        <Secao icone="≡" titulo="Descrição">
          <DescricaoDaTarefa
            descricao={descricao}
            membros={membros}
            aoSalvar={aoSalvarDescricao}
          />
        </Secao>

        <Secao
          icone="☑"
          titulo="Checklist"
          contagem={total > 0 ? `${feitos}/${total}` : undefined}
        >
          {/*
            A barra de progresso é do `ChecklistDaTarefa`, e só dele.

            Eu tinha posto outra aqui ao remodelar o card, e o resultado foram duas barras
            empilhadas medindo a mesma coisa — uma dizendo "43%" e a outra "3 / 7". É a
            régua duplicada que este repositório documenta há tempo, cometida por
            distração: o componente já trazia a sua desde antes das abas saírem.

            Quem mostra o número aqui é o título da seção, e ele responde outra pergunta —
            "quanto tem aqui dentro", sem precisar chegar até a lista.
          */}
          <ChecklistDaTarefa tarefaId={tarefaId} membros={membros} />
        </Secao>
      </div>

      <aside className="tf-lateral">
        <div className="tf-lat-bloco">
          <h4>Responsáveis</h4>
          <div className="tf-resp">
            {membros.map((m) => (
              <button
                key={m.id}
                className={`rtog${responsaveis.includes(m.id) ? ' on' : ''}`}
                title={m.nome}
                aria-pressed={responsaveis.includes(m.id)}
                onClick={() => aoAlternarResponsavel(m.id)}
              >
                <Avatar
                  className="av"
                  nome={m.nome}
                  cor={m.cor}
                  url={m.avatar}
                  style={{ '--c': m.cor } as React.CSSProperties}
                />
              </button>
            ))}
          </div>
          {responsaveis.length === 0 && <p className="tf-vazio">ninguém atribuído</p>}
        </div>

        <div className="tf-lat-bloco">{contexto}</div>

        <div className="tf-lat-bloco">
          <button className={`mdone${concluida ? ' on' : ''}`} onClick={aoAlternarConcluida}>
            <span className={`cbox${concluida ? ' on' : ''}`} style={{ pointerEvents: 'none' }}>
              <svg viewBox="0 0 12 12">
                <polyline points="2,6.4 4.6,9 10,3" />
              </svg>
            </span>
            {concluida ? `${rotuloConcluida} concluída` : 'Marcar como concluída'}
          </button>

          {/* Por último e sozinho: é a única ação da tela que não tem volta. */}
          <button className="tf-excluir" onClick={aoExcluir}>
            Excluir
          </button>
        </div>
      </aside>

      {/*
        Anexos e Atividade ocupam a largura inteira, abaixo das duas colunas.

        Eles estavam presos na coluna de conteúdo, e não deviam: a lateral é curta — quem
        responde, a janela, concluir, excluir — e acaba muito antes deles. O que sobrava
        era uma faixa vazia à direita justamente na altura do que mais precisa de espaço:
        um nome de anexo e um comentário de dez linhas.
      */}
      <div className="tf-abaixo">
        <Secao icone="⎘" titulo="Anexos" contagem={quantosAnexos > 0 ? quantosAnexos : undefined}>
          <AnexosDaTarefa tarefaId={tarefaId} />
        </Secao>

        {/*
          "Atividade", e não "Comentários": é o nome do que se procura ali — o que foi
          decidido e quando —, e a conversa é a forma que isso tem hoje.

          A contagem vem de fora, e não de uma consulta daqui: o número de comentários já
          chega no card e na frente, e buscar a conversa inteira só para contá-la seria
          pagar a lista para desenhar um dígito.
        */}
        <Secao icone="✎" titulo="Atividade" contagem={comentarios > 0 ? comentarios : undefined}>
          <Conversa dono={{ tipo: 'tarefa', id: tarefaId }} membros={membros} />
        </Secao>
      </div>
    </div>
  );
}
